import { useState } from 'react'
import { useScope } from '@/state/scope'
import { DAY_LABEL, dayOf, type SuiteDay } from '@/suites'
import { WaitingForYou, WorkUnder } from '@/components/WorkDay'
import { useAccess } from '@/state/access'
import { MemberToday } from './MemberToday'
import { useNow } from '@/hooks/useNow'
import { Link } from '@tanstack/react-router'
import {
  useAllEdges,
  useCurrentWorkspace,
  useDates,
  useMeta,
  useProjects,
  useSettings,
  useTasks,
} from '@/db/hooks'
import { withSteps } from '@/lib/steps'
import { isJob } from '@/lib/jobs'
import { META } from '@/db/schema'
import { db, mutate } from '@/runtime'
import { useListNav } from '@/hooks/useKeyboard'
import { selectToday } from '@/lib/today'
import { isTemplate } from '@/kinds/task/schema'
import { planReview } from '@/lib/review'
import { addDays, fmtMinutes, todayLocal } from '@/lib/time'
import { useUi, type EnergyChip, type TimeChip } from '@/state/ui'
import { TaskRow } from '@/components/TaskRow'
import { Button, IconButton, Segmented, cx } from '@/components/ui'
import { AskIcon, SuggestionsRow } from '@/components/Suggestions'
import { DayCards, useDayCardShowing } from '@/components/DayCards'
import { WellbeingRow } from '@/components/WellbeingRow'
import { TodayDates } from '@/components/TodayDates'
import { WhatsNew } from '@/components/WhatsNew'
import { dayShapeParts, smallest } from '@/lib/shape'
import { nextFixed } from '@/lib/dates'
import { Horizon } from '@/components/Horizon'
import { undoWithFlash } from '@/lib/undo'
import { taskActions } from '@/lib/actions'

import { useProjectOf } from '@/db/hooks'
import {
  CalendarClock,
  CalendarDays,
  ChevronRight,
  CircleDashed,
  Inbox,
  Plus,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react'

const TIMES: TimeChip[] = [15, 30, 60, 'any']
const ENERGIES: EnergyChip[] = ['low', 'medium', 'high', 'any']

/** A workspace member gets her own cut-down page; the owner's Today is below. */
/**
 * Today's route is the suite's day page (suites/index.ts `day`): Life's whole
 * Today; DevOS's Now (the code day, components/WorkDay.tsx); Blank's day's
 * tasks alone. Every open task dated today stays on this page in all three.
 */
export const TodayView = () => {
  const member = useAccess((s) => s.member)
  const scope = useScope((s) => s.ws)
  const day = dayOf(useCurrentWorkspace(), scope)
  return member ? <MemberToday /> : <OwnerToday day={day} />
}

const OwnerToday = ({ day }: { day: SuiteDay }) => {
  // the owner's life (the horizon, smoking, wellbeing, dates, the day's cards) is Life's alone
  const life = day === 'life'
  const work = day === 'work'
  const tasks = useTasks() ?? []
  const projects = useProjects() ?? []
  const settings = useSettings()
  const picker = useUi((s) => s.picker)
  const setPicker = useUi((s) => s.setPicker)
  const setQuickAdd = useUi((s) => s.setQuickAdd)
  const activeId = useUi((s) => s.activeTaskId)
  const [showAll, setShowAll] = useState(false)
  const filtering = picker.time !== 'any' || picker.energy !== 'any'
  // folded unless a filter is on, so a filtered list never hides why it is short
  const [filtersOpen, setFiltersOpen] = useState(filtering)
  const today = todayLocal()
  const now = useNow()
  const sel = selectToday(tasks, { today, cap: settings.today_cap, picker, activeId })
  const list = showAll && !activeId ? sel.all : sel.picked
  // steps sit under their parent when both are on the page: a references edge
  // from step to parent (the task page's "Make sub-tasks", Prime's subtasks)
  const edges = useAllEdges()
  const ordered = withSteps(list, edges)
  const cursor = useListNav(list.map((t) => t.id))
  const projectOf = useProjectOf()
  const byId = new Map(projects.map((p) => [p.id, p]))
  const hidden = sel.all.length - list.length

  const bookend = useDayCardShowing()
  // a pointer, not a count: things in Next with no date would otherwise be on no page at all
  const undated = tasks.some(
    (t) =>
      !t.deleted_at &&
      !isTemplate(t.spec) &&
      t.status.state === 'next' &&
      !t.spec.scheduled &&
      !t.spec.due &&
      !isJob(t),
  )
  const dates = useDates()
  // the next appointment today, when there is one: "Dentist in 1h 40m"
  const upNext = life ? nextFixed(dates, new Date(now)) : undefined
  // the recap and the next appointment are the owner's evening and diary: Life's day only
  const shape = dayShapeParts(
    sel.all,
    now,
    life ? settings.recap_push : null,
    upNext ? { title: upNext.fixed.date.spec.title, minutes: upNext.minutes } : undefined,
  )
  const first = list[0]
  const running = !!first?.status.started_at
  /**
   * When the day is too much: keep the smallest thing and push the rest to
   * tomorrow. Nothing is lost and it is one press of undo away, which is the
   * point: a bad day should cost one tap, not a triage session.
   */
  const keepOnlyOne = async () => {
    const keep = smallest(sel.all)
    if (!keep) return
    const rest = sel.all.filter((t) => t.id !== keep.id)
    await mutate.grouped(async () => {
      for (const t of rest) await taskActions.defer(t, addDays(today, 1))
    })
    useUi.getState().showFlash(`Just “${shortTitle(keep.spec.title)}” today`, 6000, {
      label: 'Undo',
      run: () => void undoWithFlash(),
    })
  }
  const dismissedAt = useMeta<string>(META.reviewDismissedAt)
  const lastReview = useMeta<string>(META.lastReviewAt)
  const review = planReview(tasks, projects, settings, today)
  const weekAgo = new Date(now - 6 * 86_400_000).toISOString()
  const showHint =
    life &&
    settings.review_hint &&
    review.total > 0 &&
    (!lastReview || lastReview < weekAgo) &&
    (!dismissedAt || dismissedAt < weekAgo)

  const rowTools = (
    <>
      <AskIcon />
      {sel.eligible > 0 && (
        <IconButton
          label={filtersOpen ? 'Hide filters' : 'Filter by time and energy'}
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
          className={cx('!min-h-8 !min-w-8', filtering && 'text-accent')}
          data-testid="today-filter"
        >
          <SlidersHorizontal size={15} aria-hidden />
        </IconButton>
      )}
    </>
  )

  return (
    <div>
      {/* the day, drawn: where you are between getting up and lights out */}
      {life && <Horizon className="mb-3" />}
      <header
        className={cx(
          'flex items-end justify-between gap-3',
          // Life has its row under the header; the lean days go straight to the list
          life || (shape && !activeId) ? 'mb-1' : 'mb-4',
        )}
      >
        <div className="min-w-0">
          <h1 className="t-display">{DAY_LABEL[day]}</h1>
          <p className="text-[15px] text-muted mt-1">
            {new Date(now).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
        <span className="flex items-center gap-1 pb-0.5">
          {/* without Life's row under it, the day's two tools sit beside the clock */}
          {!life && rowTools}
          <span className="t-title2 tabular text-fg-2 ml-1">
            {new Date(now).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
          </span>
        </span>
      </header>
      {shape && !activeId && (
        // phrases set apart, not strung together with dots
        <p className="flex flex-wrap gap-x-4 gap-y-0.5 mb-4 text-[13px] text-faint" data-testid="today-shape">
          {shape.map((part) => (
            <span key={part}>{part}</span>
          ))}
        </p>
      )}
      {/* one row: the evening's taps, and the filters folded behind a button (Life's day) */}
      {life && <WellbeingRow>{rowTools}</WellbeingRow>}
      {sel.eligible > 0 && filtersOpen && (
        // each picker named, on its own line: bare "15m 30m" and "low med high" said nothing of what they filter
        <div className="flex flex-col gap-2 mb-3 fade-in" role="group" aria-label="Filters">
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-semibold text-muted w-14 shrink-0">Time</span>
            <Segmented
              label="Time"
              value={picker.time}
              onChange={(time) => setPicker({ time })}
              options={TIMES.map((t) => ({ value: t, label: t === 'any' ? 'Any' : `${t}m` }))}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[13px] font-semibold text-muted w-14 shrink-0">Energy</span>
            <Segmented
              label="Energy"
              value={picker.energy}
              onChange={(energy) => setPicker({ energy })}
              options={ENERGIES.map((e) => ({
                value: e,
                label: e === 'any' ? 'Any' : e === 'medium' ? 'Medium' : e === 'low' ? 'Low' : 'High',
              }))}
            />
          </div>
        </div>
      )}
      {/* one slot at the top: whichever of these is the thing for right now */}
      {life && (
        <>
          <WhatsNew hideFor={bookend} />
          <DayCards />
          <TodayDates today={today} />
        </>
      )}
      {work && <WaitingForYou />}

      {/* one thing to press: starting is easier than choosing */}
      {first && !activeId && (
        <div className="flex items-center gap-2 mb-3" data-testid="today-start">
          <Button
            variant="primary"
            big
            className="flex-1 min-w-0 !gap-3"
            onClick={() => void taskActions.start(first)}
            data-testid="today-start-first"
          >
            {/* one line: the title gives way, the estimate stands apart (no dot) */}
            <span className="truncate">
              {running ? 'Back to' : 'Start'} “{shortTitle(first.spec.title)}”
            </span>
            {first.spec.estimate_minutes ? (
              <span className="shrink-0 font-normal opacity-80">
                {fmtMinutes(first.spec.estimate_minutes)}
              </span>
            ) : null}
          </Button>
          {sel.all.length > 1 && (
            <button
              type="button"
              onClick={() => void keepOnlyOne()}
              className="shrink-0 text-xs text-muted hover:text-fg min-h-11 px-2"
              data-testid="today-too-much"
            >
              Too much
            </button>
          )}
        </div>
      )}
      {list.length === 0 ? (
        <div className="text-center pt-8 pb-6 fade-in" data-testid="today-empty">
          {sel.eligible > 0 ? (
            <>
              <p className="text-muted text-sm mb-3">Nothing matches that filter.</p>
              <Button variant="chip" onClick={() => setPicker({ time: 'any', energy: 'any' })}>
                Clear it
              </Button>
            </>
          ) : (
            <>
              <p className="text-muted text-sm mb-4">Nothing planned for today yet.</p>
              <div className="flex flex-wrap justify-center gap-2">
                <Link
                  to="/inbox"
                  className="rounded-full bg-raised border border-line px-3.5 min-h-9 inline-flex items-center gap-2 text-sm hover:bg-line"
                >
                  <Inbox size={15} aria-hidden /> Pick from Inbox
                </Link>
                <Link
                  to="/upcoming"
                  className="rounded-full bg-raised border border-line px-3.5 min-h-9 inline-flex items-center gap-2 text-sm hover:bg-line"
                >
                  <CalendarDays size={15} aria-hidden /> Pull one forward
                </Link>
                <Button
                  variant="chip"
                  className="!min-h-9 !text-sm !px-3.5 gap-2"
                  onClick={() => setQuickAdd(true)}
                >
                  <Plus size={15} aria-hidden /> Add something
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <ul className="group-list" data-testid="today-list">
          {ordered.map(({ task: t, child }, i) => (
            <TaskRow
              key={t.id}
              task={t}
              index={i}
              focused={cursor === i}
              project={byId.get(projectOf.get(t.id) ?? '')}
              expanded={t.id === activeId}
              child={child}
            />
          ))}
        </ul>
      )}
      {!activeId && hidden > 0 && (
        <Button className="mt-3 w-full" onClick={() => setShowAll(true)}>
          Show the rest
        </Button>
      )}
      {showAll && !activeId && sel.all.length > sel.picked.length && (
        <Button className="mt-3 w-full" onClick={() => setShowAll(false)}>
          Show fewer
        </Button>
      )}

      {/*
        Everything that is about the day rather than in it sits under the list:
        the list is what Today is for, so it comes first on a phone.
      */}
      {!activeId && (
        <div className="mt-6 flex flex-col gap-4">
          <Nudges slipped={sel.slipped.length} undated={undated} review={!!showHint} />
          {/* 'capture' too: a capture proposal that was never applied is a suggestion,
              not a receipt, and it would otherwise have nowhere to be */}
          <SuggestionsRow types={['plan', 'schedule', 'breakdown', 'answer']} compact />
          {work && <WorkUnder />}
        </div>
      )}
    </div>
  )
}

/** Long titles make a button unreadable; the row underneath still has it in full. */
const shortTitle = (t: string) => (t.length > 24 ? `${t.slice(0, 22).trimEnd()}…` : t)

/**
 * The day's pointers as one grouped card: something slipped, things with no
 * day, the weekly review. Each is a row that goes where it can be dealt with;
 * none of them counts anything.
 */
const Nudges = ({ slipped, undated, review }: { slipped: number; undated: boolean; review: boolean }) => {
  if (!slipped && !undated && !review) return null
  const row = 'flex items-center gap-3 px-3.5 min-h-12 text-sm hover:bg-raised'
  return (
    <ul className="group-list" data-testid="today-nudges">
      {slipped > 0 && (
        <li>
          <Link to="/review" hash="slipped" className={row} data-testid="today-slipped">
            <CalendarClock size={16} className="shrink-0 text-amber-300" aria-hidden />
            <span className="flex-1">
              {slipped === 1 ? 'Something slipped' : 'Some things slipped'}: reschedule
            </span>
            <ChevronRight size={16} className="shrink-0 text-faint" aria-hidden />
          </Link>
        </li>
      )}
      {undated && (
        <li>
          <Link to="/upcoming" hash="nodate" className={row} data-testid="today-undated">
            <CircleDashed size={16} className="shrink-0 text-muted" aria-hidden />
            <span className="flex-1">Ready, but no day yet</span>
            <ChevronRight size={16} className="shrink-0 text-faint" aria-hidden />
          </Link>
        </li>
      )}
      {review && (
        <li className="flex items-center">
          <Link to="/review" className={cx(row, 'flex-1')}>
            <RotateCcw size={16} className="shrink-0 text-muted" aria-hidden />
            <span className="flex-1">A weekly review is waiting</span>
          </Link>
          <button
            type="button"
            className="text-xs text-faint hover:text-fg min-h-12 px-3.5"
            onClick={() => void db.setMeta(META.reviewDismissedAt, new Date().toISOString())}
          >
            Later
          </button>
        </li>
      )}
    </ul>
  )
}
